import {
  DEFAULT_CODE_FONT_SIZE,
  DEFAULT_SIDEBAR_LAYOUT_MODE,
  DEFAULT_UI_FONT_SIZE,
} from "@cmmd-forge/contracts/settings";

import type { FirstAccountSetupProps } from "./FirstAccountSetupTypes";
import {
  buildProviderRows,
  isSetupProjectOpenable,
  providerDisplayName,
  SETUP_CONTENT,
  SETUP_STEPS,
  type ProviderSetupRow,
  type SetupProjectRow,
  type SetupStepId,
  type UsageSource,
} from "./firstAccountSetupData";

export function stepActionLabel(stepId: SetupStepId): string {
  if (stepId === "first-run") return "Start first chat";
  return "Continue";
}

function environmentContinueBlocked(input: {
  readonly stepId: SetupStepId;
  readonly phase: FirstAccountSetupProps["environmentPhase"];
  readonly environmentId: string | null | undefined;
}): boolean {
  if (input.stepId !== "environment") return false;
  if (
    input.phase === "dead-end" ||
    input.phase === "confirm" ||
    input.phase === "ensuring" ||
    input.phase === "failed"
  ) {
    return true;
  }
  if (input.phase === "idle" || input.phase === undefined) {
    return !input.environmentId;
  }
  return false;
}

export function stepDetail(input: {
  readonly stepId: SetupStepId;
  readonly defaultDetail: string;
  readonly providerRows: readonly ProviderSetupRow[];
  readonly projects: readonly SetupProjectRow[];
}): string {
  if (input.stepId === "providers") {
    if (input.providerRows.length === 0) return "Status pending";
    return `${input.providerRows.length} provider${input.providerRows.length === 1 ? "" : "s"} loaded`;
  }
  if (input.stepId === "projects") {
    if (input.projects.length === 0) return "Import later";
    return `${input.projects.length} project${input.projects.length === 1 ? "" : "s"} loaded`;
  }
  return input.defaultDetail;
}

function selectedProjectId(
  projects: FirstAccountSetupProps["projects"],
  selectedProjectIdValue: string | null,
): string | null {
  const firstOpenableProject = projects.find(isSetupProjectOpenable) ?? null;
  const selectedProject = projects.find((project) => project.id === selectedProjectIdValue) ?? null;
  if (selectedProject !== null && isSetupProjectOpenable(selectedProject)) {
    return selectedProject.id;
  }
  return firstOpenableProject?.id ?? null;
}

function projectProviderLabel(
  projects: FirstAccountSetupProps["projects"],
  selectedProjectIdValue: string | null,
): string | null {
  const selectedProject = projects.find((project) => project.id === selectedProjectIdValue);
  if (selectedProject?.defaultProviderLabel) return selectedProject.defaultProviderLabel;
  const firstOpenable = projects.find(isSetupProjectOpenable);
  return firstOpenable?.defaultProviderLabel ?? null;
}

function providerRowName(providerRows: readonly ProviderSetupRow[]): string {
  const readyProvider = providerRows.find((provider) => provider.statusTone === "ready");
  if (readyProvider) return readyProvider.name;
  const first = providerRows[0];
  return first ? first.name : "available provider";
}

function selectedProviderLabel(
  projects: FirstAccountSetupProps["projects"],
  selectedProjectIdValue: string | null,
  providerRows: readonly ProviderSetupRow[],
): string {
  const fromProject = projectProviderLabel(projects, selectedProjectIdValue);
  if (fromProject) return providerDisplayName(fromProject);
  return providerRowName(providerRows);
}

export function deriveFirstAccountSetupView(
  props: FirstAccountSetupProps,
  state: {
    readonly activeStepIndex: number;
    readonly selectedProjectId: string | null;
  },
) {
  const providerRows = buildProviderRows(
    props.providers,
    props.cloudCodexCredential,
    props.cloudClaudeCredential,
  );
  const fallbackStep = {
    id: "usage" as const,
    label: "Agents",
    detail: "Choose accounts",
  };
  const activeStep = SETUP_STEPS[state.activeStepIndex] ?? fallbackStep;
  return {
    providerRows,
    effectiveSelectedProjectId: selectedProjectId(props.projects, state.selectedProjectId),
    selectedProviderLabel: selectedProviderLabel(
      props.projects,
      state.selectedProjectId,
      providerRows,
    ),
    theme: props.theme ?? "system",
    uiFontSize: props.uiFontSize ?? DEFAULT_UI_FONT_SIZE,
    codeFontSize: props.codeFontSize ?? DEFAULT_CODE_FONT_SIZE,
    sidebarLayoutMode: props.sidebarLayoutMode ?? DEFAULT_SIDEBAR_LAYOUT_MODE,
    activeStep,
    activeContent: SETUP_CONTENT[activeStep.id],
    canGoBack: state.activeStepIndex > 0,
    isFinalStep: state.activeStepIndex === SETUP_STEPS.length - 1,
    environmentBlocksContinue: environmentContinueBlocked({
      stepId: activeStep.id,
      phase: props.environmentPhase,
      environmentId: props.environmentId,
    }),
  };
}

export { SETUP_STEPS };
export type { UsageSource };
